"use client";

import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/italian_vineyard_aerial.png",
  alt: "Vigneti italiani rigogliosi",
  year: "2024",
  spanClass: "md:col-span-12 aspect-video"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/solid_dew_rsl_application.png",
  alt: "Applicazione Solid Dew RSL",
  year: "RSL",
  spanClass: "md:col-span-7 aspect-square"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/biodust_soil_detail.png",
  alt: "Dettaglio terreno con Biodust",
  year: "BIO",
  spanClass: "md:col-span-5 aspect-square"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/irrigation_efficiency_system.png",
  alt: "Sistema di irrigazione efficiente",
  year: "TECH",
  spanClass: "md:col-span-5 aspect-square"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/lush_crop_yield_results.png",
  alt: "Risultati raccolto rigoglioso",
  year: "ROI",
  spanClass: "md:col-span-7 aspect-square"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/sustainable_farming_panorama.png",
  alt: "Panorama agricoltura sostenibile",
  year: "2024",
  spanClass: "md:col-span-12 aspect-video"
}];
function GalleryItem({
  item,
  index
}: {
  item: (typeof galleryItems)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`relative group overflow-hidden bg-muted rounded-3xl shadow-lg ring-1 ring-white/20 ${item.spanClass} transition-all duration-1000 ease-out will-change-transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <img src={item.src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-[2.5s] ease-in-out group-hover:scale-110" loading={index < 2 ? "eager" : "lazy"} />
      
      {}
      <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center backdrop-blur-[2px]">
        <div className="flex flex-col items-center gap-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-700 ease-out">
          <div className="w-12 h-12 rounded-lg border border-white/30 flex items-center justify-center bg-ring shadow-xl">
            <Plus className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <span className="font-default text-xs font-bold tracking-widest text-white bg-ring px-4 py-1.5 rounded-lg shadow-md uppercase">
            {item.year}
          </span>
        </div>
      </div>
    </div>;
}
export default function VisualNarrative() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="224279" id="eccellenza-nel-paesaggio" className="w-full bg-background text-foreground py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div ref={headerRef} className={`mb-16 md:mb-24 max-w-2xl transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-primary mb-6 uppercase">
            Eccellenza nel Paesaggio
          </h2>
          <p className="font-default text-muted-foreground text-lg md:text-xl leading-relaxed">
            Un'esplorazione visiva dell'impatto di Solid Green Italia sul territorio. 
            Dalla rigenerazione del suolo alla gestione intelligente dell'acqua, ogni scatto racconta una storia di crescita sostenibile.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {galleryItems.map((item, index) => <GalleryItem key={item.id} item={item} index={index} />)}
        </div>
      </div>
    </section>;
}